import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CarFront,
  CircleCheck,
  FlagTriangleRight,
  Footprints,
  Mail,
  MapPin,
  Minus,
  Plus,
  UsersRound,
} from "lucide-react"
import { useMemo, useState } from "react"
import type { FormEvent } from "react"

import { DemoNotice } from "@/components/demo-notice"
import { PublicPagePending } from "@/components/loading-skeleton"
import { PublicHeader } from "@/components/public-header"
import { Button } from "@/components/ui/button"
import { formatCourseDate, formatCurrency, formatMinutes } from "@/lib/date"
import type { CartPreference } from "@/lib/domain"
import { teeTimeQueryOptions } from "@/lib/query-options"
import { cn } from "@/lib/utils"
import { startBookingCheckoutFn } from "@/server/functions"
import type { getTeeTimeFn } from "@/server/functions"

type BookingSearch = {
  players: number
  cart: CartPreference
}

export const Route = createFileRoute("/book/$teeTimeId")({
  validateSearch: (search: Record<string, unknown>): BookingSearch => {
    const rawPlayers = Number(search.players)

    return {
      players:
        Number.isInteger(rawPlayers) && rawPlayers >= 1 && rawPlayers <= 4
          ? rawPlayers
          : 2,
      cart: search.cart === "walking" ? "walking" : "riding",
    }
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(teeTimeQueryOptions(params.teeTimeId)),
  head: () => ({
    meta: [{ title: "Confirm your group · Groundline" }],
  }),
  pendingComponent: PublicPagePending,
  component: ConfirmBooking,
})

function ConfirmBooking() {
  const { teeTimeId } = Route.useParams()
  const { data } = useSuspenseQuery(teeTimeQueryOptions(teeTimeId))
  const search = Route.useSearch()

  if (!data) {
    return <MissingTeeTime />
  }

  if (!data.teeTime.isBookable) {
    return (
      <UnavailableTeeTime
        viewer={data.viewer}
        courseSlug={data.course.slug}
        date={data.teeTime.date}
      />
    )
  }

  return <BookingForm data={data} initial={search} />
}

function MissingTeeTime() {
  return (
    <>
      <PublicHeader viewer={null} />
      <main className="site-frame grid min-h-[70vh] place-items-center py-16">
        <div className="text-center">
          <FlagTriangleRight className="mx-auto size-7 text-muted-foreground" />
          <h1 className="page-title">That tee time is gone.</h1>
          <p className="page-lede mx-auto">
            It may have moved outside the demo window. Return to the live sheet
            to pick another.
          </p>
          <Button render={<Link to="/" />} className="mt-6">
            Back to availability
          </Button>
        </div>
      </main>
    </>
  )
}

function UnavailableTeeTime({
  viewer,
  courseSlug,
  date,
}: {
  viewer: NonNullable<Awaited<ReturnType<typeof getTeeTimeFn>>>["viewer"]
  courseSlug: string
  date: string
}) {
  return (
    <>
      <PublicHeader viewer={viewer} />
      <DemoNotice />
      <main className="site-frame grid min-h-[70vh] place-items-center py-16">
        <div className="max-w-[580px] text-center">
          <UsersRound className="mx-auto size-7 text-muted-foreground" />
          <h1 className="page-title">That tee time has closed.</h1>
          <p className="page-lede mx-auto">
            It may have passed, been blocked, or filled before confirmation. The
            live rail has the latest open times for this course and date.
          </p>
          <Button
            render={<Link to="/" search={{ course: courseSlug, date }} />}
            variant="accent"
            className="mt-6"
          >
            Choose another time
          </Button>
        </div>
      </main>
    </>
  )
}

function BookingForm({
  data,
  initial,
}: {
  data: NonNullable<Awaited<ReturnType<typeof getTeeTimeFn>>>
  initial: BookingSearch
}) {
  const maxPlayers = Math.max(1, Math.min(4, data.teeTime.spotsLeft))
  const [playerCount, setPlayerCount] = useState(
    Math.min(initial.players, maxPlayers)
  )
  const [cart, setCart] = useState<CartPreference>(initial.cart)
  const [names, setNames] = useState<Array<string>>(() =>
    Array.from({ length: maxPlayers }, (_, index) =>
      index === 0 ? (data.viewer?.name ?? "") : ""
    )
  )
  const [email, setEmail] = useState(data.viewer?.email ?? "")
  const [notes, setNotes] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const visibleNames = names.slice(0, playerCount)
  const cartCount = cart === "riding" ? Math.ceil(playerCount / 2) : 0
  const greenFees = data.teeTime.basePriceCents * playerCount
  const total = greenFees + cartCount * data.course.cartFeeCents
  const isLocalSimulation = data.checkoutMode === "local-simulation"
  const allNamesPresent = useMemo(
    () => visibleNames.every((name) => name.trim().length > 0),
    [visibleNames]
  )

  function updateName(index: number, value: string) {
    setNames((current) =>
      current.map((name, nameIndex) => (nameIndex === index ? value : name))
    )
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")

    if (!allNamesPresent) {
      setError("Add a name for every player in the group.")
      return
    }

    if (!email.trim()) {
      setError("Add a contact email for the lead player.")
      return
    }

    setSubmitting(true)

    try {
      const result = await startBookingCheckoutFn({
        data: {
          teeTimeId: data.teeTime.id,
          playerNames: visibleNames,
          leadEmail: email,
          cartPreference: cart,
          notes: notes || undefined,
        },
      })

      if (result.ok) {
        window.location.assign(result.checkoutUrl)
        return
      }

      if (result.reason === "CAPACITY_CHANGED") {
        setError(
          "Another group just took those spots. Return to availability and choose an updated time."
        )
      } else if (
        result.reason === "PAYMENTS_NOT_CONFIGURED" ||
        result.reason === "GATEWAY_UNAVAILABLE"
      ) {
        setError(
          "Stripe test checkout is temporarily unavailable. No payment was submitted; please try again."
        )
      } else {
        setError("That tee time is no longer available.")
      }
    } catch {
      setError("The booking could not be saved. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PublicHeader viewer={data.viewer} />
      <DemoNotice />
      <main className="site-frame py-10 sm:py-14">
        <Button
          render={
            <Link
              to="/"
              search={{
                course: data.course.slug,
                date: data.teeTime.date,
              }}
            />
          }
          variant="ghost"
          className="-ml-3"
        >
          <ArrowLeft data-icon="inline-start" />
          Back to tee times
        </Button>

        <div className="mt-5 grid gap-7 lg:grid-cols-[minmax(0,1fr)_350px] lg:items-start">
          <form onSubmit={submit} className="min-w-0">
            <span className="eyebrow">
              <CircleCheck className="size-4" />
              Confirm the starter tag
            </span>
            <h1 className="page-title">Who is playing?</h1>
            <p className="page-lede">
              {isLocalSimulation
                ? "Add the group and a contact email, then confirm the documented local pay-at-course simulation. No external payment request is made."
                : "Add the group and a contact email, then complete payment in Stripe’s hosted test checkout. The tee time is held while you pay."}
            </p>

            <section className="ruled-panel mt-8 overflow-hidden">
              <div className="ruled-panel__head">
                <div>
                  <span className="eyebrow">Group size</span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {data.teeTime.spotsLeft} spots currently available
                  </p>
                </div>
                <div className="flex min-h-11 items-center gap-1 rounded-[9px] border border-border bg-muted px-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Remove a player"
                    disabled={playerCount <= 1}
                    onClick={() => setPlayerCount((value) => value - 1)}
                  >
                    <Minus />
                  </Button>
                  <span className="min-w-24 text-center text-sm font-semibold">
                    {playerCount} {playerCount === 1 ? "player" : "players"}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Add a player"
                    disabled={playerCount >= maxPlayers}
                    onClick={() => setPlayerCount((value) => value + 1)}
                  >
                    <Plus />
                  </Button>
                </div>
              </div>
              <div>
                {Array.from({ length: playerCount }, (_, index) => (
                  <div
                    key={index}
                    className="rule-row grid gap-2 px-5 py-4 sm:grid-cols-[120px_1fr] sm:items-center"
                  >
                    <label
                      htmlFor={`player-${index}`}
                      className="text-xs font-semibold"
                    >
                      {index === 0 ? "Lead player" : `Player ${index + 1}`}
                    </label>
                    <input
                      id={`player-${index}`}
                      className="control"
                      autoComplete={index === 0 ? "name" : "off"}
                      placeholder={
                        index === 0 ? "Your full name" : "Guest name"
                      }
                      value={names[index]}
                      maxLength={80}
                      required
                      onChange={(event) =>
                        updateName(index, event.target.value)
                      }
                    />
                  </div>
                ))}
                <div className="rule-row grid gap-2 px-5 py-4 sm:grid-cols-[120px_1fr] sm:items-start">
                  <label
                    htmlFor="lead-email"
                    className="pt-3 text-xs font-semibold"
                  >
                    Contact email
                  </label>
                  <div>
                    <input
                      id="lead-email"
                      type="email"
                      className="control"
                      autoComplete="email"
                      inputMode="email"
                      maxLength={254}
                      placeholder="you@example.com"
                      value={email}
                      aria-describedby="lead-email-hint"
                      required
                      onChange={(event) => setEmail(event.target.value)}
                    />
                    <p
                      id="lead-email-hint"
                      className="mt-2 text-xs leading-5 text-muted-foreground"
                    >
                      Used as the course contact. This demo does not send email.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="ruled-panel mt-5 overflow-hidden">
              <div className="ruled-panel__head">
                <div>
                  <span className="eyebrow">Transport</span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Shared carts seat two players
                  </p>
                </div>
              </div>
              <div className="grid gap-3 p-5 sm:grid-cols-2">
                {[
                  {
                    value: "walking" as const,
                    title: "Walk the course",
                    copy: "No cart added",
                    icon: Footprints,
                  },
                  {
                    value: "riding" as const,
                    title: "Shared carts",
                    copy: `${formatCurrency(
                      data.course.cartFeeCents,
                      data.course.currency
                    )} each`,
                    icon: CarFront,
                  },
                ].map(({ value, title, copy, icon: Icon }) => (
                  <label
                    key={value}
                    className={cn(
                      "choice-card flex min-h-20 cursor-pointer items-center gap-3 rounded-[10px] border p-4",
                      cart === value
                        ? "choice-card--selected"
                        : "bg-paper border-border hover:border-input"
                    )}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      name="transport"
                      value={value}
                      checked={cart === value}
                      onChange={() => setCart(value)}
                    />
                    <span className="grid size-10 place-items-center rounded-[8px] bg-secondary text-primary">
                      <Icon className="size-[18px]" />
                    </span>
                    <span>
                      <strong className="block text-sm">{title}</strong>
                      <small className="mt-1 block text-xs text-muted-foreground">
                        {copy}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
              <div className="rule-row field border-t border-border p-5">
                <label htmlFor="notes">Note for the pro shop (optional)</label>
                <textarea
                  id="notes"
                  className="control"
                  maxLength={500}
                  value={notes}
                  placeholder="Accessibility, rental, or group note"
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </section>

            {error ? (
              <p className="action-error mt-5" role="alert">
                {error}
              </p>
            ) : null}

            {!data.viewer ? (
              <div className="groundline-info mt-5 flex items-start gap-3 rounded-[8px] border p-4 text-sm">
                <Mail className="mt-0.5 size-5 shrink-0" />
                <div>
                  <strong className="font-semibold">Book as a guest.</strong>
                  <p className="mt-1 leading-6">
                    No account or password is required. After confirmation, save
                    the booking reference on your confirmation page.
                  </p>
                </div>
              </div>
            ) : null}

            <Button
              type="submit"
              variant="accent"
              size="lg"
              className="mt-6 w-full sm:w-auto"
              disabled={submitting || !allNamesPresent || !email.trim()}
            >
              {submitting
                ? isLocalSimulation
                  ? "Confirming local booking…"
                  : "Opening Stripe checkout…"
                : isLocalSimulation
                  ? "Confirm demo booking"
                  : data.viewer
                    ? "Pay & confirm booking"
                    : "Pay & confirm as guest"}
              <ArrowRight data-icon="inline-end" />
            </Button>
          </form>

          <aside className="ruled-panel overflow-hidden lg:sticky lg:top-[94px]">
            <div className="bg-primary p-5 text-white">
              <span className="text-[0.65rem] font-bold tracking-[0.13em] text-[#b8cad3] uppercase">
                Starter tag
              </span>
              <strong className="mt-2 block font-display text-[2.35rem] leading-none font-semibold uppercase">
                {formatMinutes(data.teeTime.startMinutes)}
              </strong>
              <span className="mt-2 block text-xs text-[#b8cad3]">
                {formatCourseDate(data.teeTime.date)}
              </span>
            </div>
            <div className="grid gap-4 p-5 text-sm">
              <div className="flex gap-3">
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span>
                  <strong className="block">{data.course.name}</strong>
                  <small className="mt-1 block text-muted-foreground">
                    {data.course.location}
                  </small>
                </span>
              </div>
              <div className="flex gap-3">
                <CalendarDays className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span>
                  <strong className="block">{data.teeTime.holes} holes</strong>
                  <small className="mt-1 block text-muted-foreground">
                    {playerCount} player group
                  </small>
                </span>
              </div>
              <div className="flex gap-3">
                <UsersRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <span>
                  <strong className="block">
                    {cart === "riding"
                      ? `${cartCount} shared ${
                          cartCount === 1 ? "cart" : "carts"
                        }`
                      : "Walking"}
                  </strong>
                  <small className="mt-1 block text-muted-foreground">
                    Transport preference
                  </small>
                </span>
              </div>
              <div className="mt-1 border-t border-dashed border-border pt-4">
                <div className="flex justify-between gap-3 text-xs text-muted-foreground">
                  <span>Green fees</span>
                  <span>{formatCurrency(greenFees, data.course.currency)}</span>
                </div>
                {cart === "riding" ? (
                  <div className="mt-2 flex justify-between gap-3 text-xs text-muted-foreground">
                    <span>Carts</span>
                    <span>
                      {formatCurrency(
                        cartCount * data.course.cartFeeCents,
                        data.course.currency
                      )}
                    </span>
                  </div>
                ) : null}
                <div className="mt-4 flex items-end justify-between gap-3">
                  <strong>Demo total</strong>
                  <strong className="font-display text-[2rem] leading-none font-semibold">
                    {formatCurrency(total, data.course.currency)}
                  </strong>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </>
  )
}
